import { liesZahl } from "./zahlformat";

/*
  Den Baum einer Formel ausrechnen.

  Dieses Modul kennt weder Yjs noch die Oberfläche noch das ganze Blatt. Es
  bekommt eine `umgebung`, die auf Nachfrage den Wert einer Zelle liefert —
  wer das Blatt zusammenhält und Ringschlüsse erkennt, steht in
  `tabellenrechnung.js`.

  ---------------------------------------------------------------
  Was ein Wert ist
  ---------------------------------------------------------------

      { art: "zahl",      wert: 42 }
      { art: "text",      wert: "Radwege" }
      { art: "wahrheit",  wert: true }
      { art: "leer" }
      { art: "fehler",    code: "#DIV/0!" }

  Eine leere Zelle ist ausdrücklich NICHT die Null. Sie rechnet zwar wie
  eine Null (`=A1+1` ergibt 1), zählt in MITTELWERT aber nicht mit — sonst
  würde ein Durchschnitt über zehn Zeilen, von denen drei leer sind, still
  zu klein.
*/

export const FEHLER = {
  WERT: "#WERT!",
  DIVISION: "#DIV/0!",
  NAME: "#NAME?",
  BEZUG: "#BEZUG!",
  ZIRKEL: "#ZIRKEL!",
  FORMEL: "#FORMEL?",
};

/*
  Die Erklärung zum kurzen Zeichen.

  In der Zelle ist für mehr kein Platz, aber `#DIV/0!` allein ist für
  jemanden, der zweimal im Jahr eine Tabelle anfasst, keine Auskunft. Der
  ganze Satz steht deshalb in der Statuszeile unter dem Blatt.
*/
const ERKLAERUNGEN = {
  "#WERT!": "Hier wird mit etwas gerechnet, das keine Zahl ist.",
  "#DIV/0!": "Hier wird durch null geteilt.",
  "#NAME?": "Diesen Funktionsnamen gibt es nicht.",
  "#BEZUG!": "Die Zelle, auf die gezeigt wurde, gibt es nicht mehr.",
  "#ZIRKEL!": "Ringschluss: Die Formel rechnet über Umwege mit sich selbst.",
  "#FORMEL?": "Diese Formel ist nicht zu lesen — fehlt eine Klammer?",
};

export function istFehler(wert) {
  return !!wert && wert.art === "fehler";
}

export function fehlertext(code) {
  return ERKLAERUNGEN[code] || "Diese Formel geht nicht auf.";
}

export function fehler(code) {
  return { art: "fehler", code };
}

export const LEER = { art: "leer" };

export function zahl(wert) {
  if (!Number.isFinite(wert)) return fehler(FEHLER.WERT);
  return { art: "zahl", wert };
}

/*
  Aus einem Wert eine Zahl machen — für alles, was rechnet.

  Text wird gedeutet, wenn er sich als Zahl lesen lässt: Wer „1.234,50" in
  eine Textspalte getippt hat und später damit rechnet, soll nicht an einer
  Formalität scheitern. Wahrheitswerte zählen wie in Excel als 1 und 0.
*/
function alsZahl(wert) {
  if (!wert || wert.art === "leer") return 0;
  if (wert.art === "zahl") return wert.wert;
  if (wert.art === "wahrheit") return wert.wert ? 1 : 0;
  if (wert.art === "text") {
    const gedeutet = liesZahl(wert.wert);
    return gedeutet === null ? null : gedeutet;
  }
  return null;
}

function alsWahrheit(wert) {
  if (!wert || wert.art === "leer") return false;
  if (wert.art === "wahrheit") return wert.wert;
  if (wert.art === "zahl") return wert.wert !== 0;
  if (wert.art === "text") return wert.wert !== "";
  return false;
}

/* ---------------------------------------------------------------
   Auswerten
   --------------------------------------------------------------- */

/*
  `umgebung` braucht genau zwei Dinge:

      holeZelle(spalte, zeile) → Wert
      holeBereich(vonBezug, bisBezug) → Wert[]

  Beide zählen ab null. Der Rest — welche Zelle es überhaupt gibt, wie tief
  die Rekursion geht — ist Sache des Aufrufers.
*/
export function werteAus(knoten, umgebung) {
  if (!knoten) return fehler(FEHLER.FORMEL);

  switch (knoten.art) {
    case "zahl":
      return zahl(knoten.wert);

    case "text":
      return { art: "text", wert: knoten.wert };

    case "fehler":
      return fehler(knoten.code);

    case "bezug":
      return umgebung.holeZelle(knoten.bezug.spalte, knoten.bezug.zeile);

    case "bereich":
      /*
        Ein Bereich für sich allein ist kein Wert. `=B2:B4` in einer Zelle
        ergibt in Excel ebenfalls einen Fehler — Bereiche gehören in
        Funktionen.
      */
      return fehler(FEHLER.WERT);

    case "eins":
      return werteEins(knoten, umgebung);

    case "zwei":
      return werteZwei(knoten, umgebung);

    case "aufruf":
      return werteAufruf(knoten, umgebung);

    default:
      return fehler(FEHLER.FORMEL);
  }
}

function werteEins(knoten, umgebung) {
  const innen = werteAus(knoten.wert, umgebung);
  if (istFehler(innen)) return innen;
  const z = alsZahl(innen);
  if (z === null) return fehler(FEHLER.WERT);
  return zahl(knoten.zeichen === "-" ? -z : z);
}

function werteZwei(knoten, umgebung) {
  const links = werteAus(knoten.links, umgebung);
  if (istFehler(links)) return links;
  const rechts = werteAus(knoten.rechts, umgebung);
  if (istFehler(rechts)) return rechts;

  if (["=", "<>", "<", ">", "<=", ">="].includes(knoten.zeichen)) {
    return vergleiche(knoten.zeichen, links, rechts);
  }

  const a = alsZahl(links);
  const b = alsZahl(rechts);
  if (a === null || b === null) return fehler(FEHLER.WERT);

  switch (knoten.zeichen) {
    case "+":
      return zahl(a + b);
    case "-":
      return zahl(a - b);
    case "*":
      return zahl(a * b);
    case "/":
      if (b === 0) return fehler(FEHLER.DIVISION);
      return zahl(a / b);
    case "^":
      return zahl(a ** b);
    default:
      return fehler(FEHLER.FORMEL);
  }
}

/*
  Vergleiche.

  Zwei Texte werden als Texte verglichen, alles andere als Zahlen. Ein
  Vergleich zwischen Text und Zahl gilt als ungleich statt als Fehler —
  `WENN(A1="ja";…)` soll auch dann arbeiten, wenn in A1 gerade eine Zahl
  steht.
*/
function vergleiche(zeichen, links, rechts) {
  const beideText = links.art === "text" && rechts.art === "text";

  if (beideText) {
    const a = links.wert.toLowerCase();
    const b = rechts.wert.toLowerCase();
    return { art: "wahrheit", wert: vergleicheWerte(zeichen, a, b) };
  }

  const a = alsZahl(links);
  const b = alsZahl(rechts);
  if (a === null || b === null) {
    if (zeichen === "=") return { art: "wahrheit", wert: false };
    if (zeichen === "<>") return { art: "wahrheit", wert: true };
    return fehler(FEHLER.WERT);
  }
  return { art: "wahrheit", wert: vergleicheWerte(zeichen, a, b) };
}

function vergleicheWerte(zeichen, a, b) {
  switch (zeichen) {
    case "=":
      return a === b;
    case "<>":
      return a !== b;
    case "<":
      return a < b;
    case ">":
      return a > b;
    case "<=":
      return a <= b;
    case ">=":
      return a >= b;
    default:
      return false;
  }
}

/* ---------------------------------------------------------------
   Die sechs Funktionen
   --------------------------------------------------------------- */

/*
  Ein Argument zu einer Liste von Werten machen.

  Ein Bereich wird aufgefaltet, alles andere ist eine Liste mit einem
  Eintrag. Nur so kann `SUMME(B2:B4; 100; C1)` dasselbe Verfahren für alle
  drei Argumente benutzen.
*/
function alsListe(knoten, umgebung) {
  if (knoten && knoten.art === "bereich") {
    return umgebung.holeBereich(knoten.von, knoten.bis);
  }
  return [werteAus(knoten, umgebung)];
}

/*
  Die Zahlen aus einer Argumentliste.

  Text und Leeres werden ÜBERGANGEN, nicht als Fehler behandelt — genau wie
  in Excel. Eine Spalte mit einer Überschrift darüber lässt sich damit
  einfach ganz summieren, ohne dass die Überschrift stört.

  Ein Fehler wird dagegen weitergereicht: Wer über eine kaputte Zelle
  summiert, soll das sehen und keine zu kleine Summe bekommen.
*/
function zahlenAus(knoten, umgebung) {
  const zahlen = [];
  for (const teil of knoten) {
    for (const wert of alsListe(teil, umgebung)) {
      if (istFehler(wert)) return { fehler: wert };
      if (!wert || wert.art === "leer") continue;
      if (wert.art === "text") continue;
      const z = alsZahl(wert);
      if (z === null) continue;
      zahlen.push(z);
    }
  }
  return { zahlen };
}

const TAFEL = {
  SUMME(argumente, umgebung) {
    const { zahlen, fehler: f } = zahlenAus(argumente, umgebung);
    if (f) return f;
    return zahl(zahlen.reduce((a, b) => a + b, 0));
  },

  MITTELWERT(argumente, umgebung) {
    const { zahlen, fehler: f } = zahlenAus(argumente, umgebung);
    if (f) return f;
    /* Ohne Zahlen kein Durchschnitt — in Excel gibt das ebenfalls einen Fehler. */
    if (zahlen.length === 0) return fehler(FEHLER.DIVISION);
    return zahl(zahlen.reduce((a, b) => a + b, 0) / zahlen.length);
  },

  ANZAHL(argumente, umgebung) {
    const { zahlen, fehler: f } = zahlenAus(argumente, umgebung);
    if (f) return f;
    return zahl(zahlen.length);
  },

  MIN(argumente, umgebung) {
    const { zahlen, fehler: f } = zahlenAus(argumente, umgebung);
    if (f) return f;
    if (zahlen.length === 0) return zahl(0);
    return zahl(Math.min(...zahlen));
  },

  MAX(argumente, umgebung) {
    const { zahlen, fehler: f } = zahlenAus(argumente, umgebung);
    if (f) return f;
    if (zahlen.length === 0) return zahl(0);
    return zahl(Math.max(...zahlen));
  },

  /*
    WENN(Bedingung; Dann; Sonst)

    Der dritte Teil darf fehlen; dann bleibt die Zelle leer. Nur der Zweig,
    der gilt, wird ausgerechnet — sonst meldete `WENN(B1=0;0;A1/B1)` eine
    Division durch null, obwohl genau die abgefangen werden sollte.
  */
  WENN(argumente, umgebung) {
    if (argumente.length < 2 || argumente.length > 3) return fehler(FEHLER.WERT);

    const bedingung = werteAus(argumente[0], umgebung);
    if (istFehler(bedingung)) return bedingung;

    if (alsWahrheit(bedingung)) return werteAus(argumente[1], umgebung);
    if (argumente.length === 3) return werteAus(argumente[2], umgebung);
    return LEER;
  },
};

function werteAufruf(knoten, umgebung) {
  const funktion = TAFEL[knoten.name];
  if (!funktion) return fehler(FEHLER.NAME);
  if (knoten.name !== "WENN" && knoten.argumente.length === 0) {
    return fehler(FEHLER.WERT);
  }
  return funktion(knoten.argumente, umgebung);
}

/*
  Der Wert, wie er in der Zelle steht — Zahl zum Formatieren, sonst Text.
  Die Oberfläche entscheidet danach, ob eine Zahl als Euro oder Prozent
  erscheint.
*/
export function alsAnzeige(wert) {
  if (!wert || wert.art === "leer") return { zahl: null, text: "" };
  if (wert.art === "fehler") return { zahl: null, text: wert.code };
  if (wert.art === "wahrheit") return { zahl: null, text: wert.wert ? "WAHR" : "FALSCH" };
  if (wert.art === "text") return { zahl: null, text: wert.wert };
  return { zahl: wert.wert, text: null };
}
